/**
 * What the listing form knows about itself while it is being filled in.
 *
 * The posting page shows the row the listing will occupy on the board as the
 * company types, and a short list of what is still missing. Both are derived
 * from the same form state the validator reads, so the preview can never
 * disagree with the errors the form raises on submit.
 */

import { getCategory, getSubcategory } from '@bidtohire/shared';

import { isHttpUrl, parseWholeNumber, type JobFormState } from './job-form';

/* -------------------------------------------------------------------------- */
/* Requirements                                                               */
/* -------------------------------------------------------------------------- */

/** One line of the "Required" checklist beside the form. */
export interface Requirement {
  readonly key: string;
  readonly label: string;
  readonly done: boolean;
}

function hasSalaryRange(state: JobFormState): boolean {
  const min = parseWholeNumber(state.salaryMin);
  const max = parseWholeNumber(state.salaryMax);
  return min !== undefined && max !== undefined && min <= max;
}

function hasLocation(state: JobFormState): boolean {
  if (state.workplaceType === 'REMOTE') return true;
  return state.locations.some(
    (location) => /^[A-Za-z]{2}$/.test(location.countryIso2.trim()) && location.city.trim() !== '',
  );
}

/**
 * The five things a listing may not be published without, in the order the
 * form asks for them.
 */
export function listingRequirements(state: JobFormState): readonly Requirement[] {
  return [
    {
      key: 'identity',
      label: 'Title, category, specialism',
      done: state.title.trim() !== '' && state.category !== '' && state.subcategory !== '',
    },
    {
      key: 'applyUrl',
      label: 'Link to your posting',
      done: isHttpUrl(state.applyUrl.trim()),
    },
    {
      key: 'description',
      label: 'Job description',
      done: state.descriptionText.trim().length >= 40,
    },
    {
      key: 'shape',
      label: 'Level and workplace',
      done: state.seniority !== '' && state.employmentType.length > 0 && hasLocation(state),
    },
    { key: 'salary', label: 'Salary range', done: hasSalaryRange(state) },
  ];
}

/** How far along the form is, 0–1, for the completion hairline. */
export function completionRatio(requirements: readonly Requirement[]): number {
  if (requirements.length === 0) return 0;
  const done = requirements.filter((requirement) => requirement.done).length;
  return done / requirements.length;
}

/** Which cards are finished, so a completed section can recede. */
export interface SectionCompletion {
  readonly role: boolean;
  readonly shape: boolean;
  readonly pay: boolean;
}

export function sectionCompletion(state: JobFormState): SectionCompletion {
  const requirements = listingRequirements(state);
  const done = (key: string): boolean =>
    requirements.find((requirement) => requirement.key === key)?.done === true;
  return {
    role: done('identity') && done('applyUrl') && done('description'),
    shape: done('shape'),
    pay: done('salary'),
  };
}

/* -------------------------------------------------------------------------- */
/* Money, as the board writes it                                              */
/* -------------------------------------------------------------------------- */

const SYMBOLS: Readonly<Record<string, string>> = { USD: '$', GBP: '£', EUR: '€' };

function symbolFor(currency: string): string {
  return SYMBOLS[currency.trim().toUpperCase()] ?? '';
}

/** "120000" → "120,000". Grouping is applied to whatever digits are present. */
export function groupDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** A single figure at board length: 120000 → "$120k", 1500 → "$1.5k". */
export function compactAmount(dollars: number, currency = 'USD'): string {
  const symbol = symbolFor(currency);
  const suffix = currency.trim().toUpperCase() === 'USD' || symbol !== '' ? '' : ` ${currency}`;
  const short = (value: number, unit: string): string => {
    const rounded = Math.round(value * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}${unit}`;
  };
  if (dollars >= 1_000_000) return `${symbol}${short(dollars / 1_000_000, 'm')}${suffix}`;
  if (dollars >= 1_000) return `${symbol}${short(dollars / 1_000, 'k')}${suffix}`;
  return `${symbol}${dollars.toLocaleString('en-US')}${suffix}`;
}

/**
 * The salary chip on the row: "$120k–$160k", or nothing at all until both
 * ends of the range are readable numbers.
 */
export function compactRange(state: JobFormState): string | undefined {
  const min = parseWholeNumber(state.salaryMin);
  const max = parseWholeNumber(state.salaryMax);
  if (min === undefined || max === undefined) return undefined;
  if (min > max) return undefined;
  const currency = state.salaryCurrency.trim() === '' ? 'USD' : state.salaryCurrency;
  if (min === max) return compactAmount(min, currency);
  return `${compactAmount(min, currency)}–${compactAmount(max, currency)}`;
}

/* -------------------------------------------------------------------------- */
/* The row                                                                    */
/* -------------------------------------------------------------------------- */

/** Everything the preview row shows, or `undefined` where it is not known yet. */
export interface PreviewRow {
  readonly title: string | undefined;
  readonly scopePath: string | undefined;
  readonly specialismName: string | undefined;
  readonly salary: string | undefined;
  readonly location: string | undefined;
}

/** "Chicago, IL", or the first location that names anything at all. */
export function previewLocation(state: JobFormState): string | undefined {
  if (state.workplaceType === 'REMOTE' && state.locations.every((row) => row.city.trim() === '')) {
    return 'Remote';
  }
  const first = state.locations.find(
    (row) => row.city.trim() !== '' || row.countryIso2.trim() !== '',
  );
  if (first === undefined) return undefined;
  const parts = [first.city.trim(), first.region.trim(), first.countryIso2.trim().toUpperCase()]
    .filter((part) => part !== '')
    .slice(0, 2);
  return parts.length === 0 ? undefined : parts.join(', ');
}

export function previewRow(state: JobFormState): PreviewRow {
  const category = state.category === '' ? undefined : getCategory(state.category);
  const subcategory =
    state.category === '' || state.subcategory === ''
      ? undefined
      : getSubcategory(state.category, state.subcategory);

  const title = state.title.trim();
  const scopePath =
    category === undefined
      ? undefined
      : subcategory === undefined
        ? category.name
        : `${category.name} → ${subcategory.name}`;

  return {
    title: title === '' ? undefined : title,
    scopePath,
    specialismName: subcategory?.name,
    salary: compactRange(state),
    location: previewLocation(state),
  };
}
